import type { AppThunk } from "@/store/store";
import type { GUID } from "@faro-lotv/foundation";
import type {
  CaptureTreeEntityRevision,
  ProjectApi,
  RegistrationEdgeRevision,
  RegistrationRevision,
} from "@faro-lotv/service-wires";
import { selectRegistrationRevision } from "../store/revision/revision-entity-selectors";
import {
  addRevisionData,
  setAreEdgesLoading,
  setRevisionData,
} from "../store/revision/revision-slice";

type LoadRevisionDataThunkParams = {
  /**
   * The ID of the revision to load.
   * If not provided, the latest draft revision will be loaded.
   */
  revisionId?: GUID;

  /**
   * The API client to load the revision data.
   */
  projectApiClient: ProjectApi;

  /**
   * If true, forces a full reload of the revision data, ignoring any cached data.
   *
   * @default false
   */
  fullReload?: boolean;

  /**
   * An optional AbortSignal to cancel the loading operation.
   */
  signal?: AbortSignal;
};

type LoadRevisionDataResult = {
  /** The loaded revision. Can be undefined when no open draft revision exists. */
  revision?: RegistrationRevision;

  /** The loaded entities for the revision. */
  entities?: CaptureTreeEntityRevision[];

  /** The loaded edges for the revision. */
  edges?: RegistrationEdgeRevision[];
};

/**
 * Loads revision object, entities and edges.
 * By default, only reloads entities and edges when the revision was modified since the last load.
 * Use `fullReload` to force a full reload of the revision data, ignoring any cached data.
 *
 * @returns the loaded revision, entities and edges.
 */
export function loadRevisionDataThunk({
  revisionId,
  projectApiClient,
  fullReload = false,
  signal,
}: LoadRevisionDataThunkParams): AppThunk<Promise<LoadRevisionDataResult>> {
  return async (dispatch, getState) => {
    const previousRevision = selectRegistrationRevision(getState());

    const revision = await loadRevision({
      revisionId,
      projectApiClient,
      signal,
    });

    const isNewRevision =
      !previousRevision || previousRevision.id !== revision?.id;

    if (!revision) {
      // There are rare cases where no open draft revision exists, e.g. in older projects

      // Clear data in store
      dispatch(
        setRevisionData({ revision: undefined, entities: [], edges: [] }),
      );

      return {};
    }

    const lastModifiedInDbAfter =
      fullReload || isNewRevision ? undefined : previousRevision.modifiedAt;
    const isRevisionModified =
      lastModifiedInDbAfter &&
      new Date(lastModifiedInDbAfter).valueOf() <
        new Date(revision.modifiedAt).valueOf();

    if (fullReload || isNewRevision || isRevisionModified) {
      const [entities, edges] = await Promise.all([
        await projectApiClient.getCaptureTreeForRegistrationRevision(
          revision.id,
          {
            signal,
            lastModifiedInDbAfter,
            itemsPerPage: 1000,
          },
        ),
        await projectApiClient.getRegistrationEdgesForRevision(revision.id, {
          signal,
          lastModifiedInDbAfter,
          itemsPerPage: 1000,
        }),
      ]);

      if (lastModifiedInDbAfter) {
        // Amend new data
        dispatch(
          addRevisionData({
            revision,
            entities,
            edges,
          }),
        );
      } else {
        // Fully replace previous data
        dispatch(
          setRevisionData({
            revision,
            entities,
            edges,
          }),
        );
      }

      // Notify other parts of the app that the edges should be available
      dispatch(setAreEdgesLoading(false));

      return {
        revision,
        entities,
        edges,
      };
    }

    // No changes to revision, entities and edges don't need to be reloaded
    dispatch(addRevisionData({ revision }));

    return {
      revision,
    };
  };
}

type LoadRevisionParams = {
  /**
   * The ID of the revision to load.
   * If not provided, the latest draft revision will be loaded.
   */
  revisionId?: GUID;

  /** The API client to use for loading the revision. */
  projectApiClient: ProjectApi;

  /** An optional AbortSignal to cancel the loading operation. */
  signal?: AbortSignal;
};

/**
 * @returns the specified revision or the latest draft revision if no ID was provided.
 */
async function loadRevision({
  revisionId,
  projectApiClient,
  signal,
}: LoadRevisionParams): Promise<RegistrationRevision | undefined> {
  if (revisionId) {
    // Load the specific revision
    const revision = await projectApiClient.getRegistrationRevision(
      revisionId,
      signal,
    );

    return revision;
  }

  // Automatically pick latest draft if no revision has been specified
  return await projectApiClient.getLastDraftRevision(signal);
}
